/**
 * What kind of machine is this, and how much may a scene spend on it.
 *
 * Two questions, one module, and both answers are **pure functions of a
 * described device** with thin live readers on top. That split is the whole
 * design: `matchMedia` and `devicePixelRatio` are untestable ambient globals
 * that do not exist under jsdom, so everything that decides anything takes its
 * inputs as arguments and the only code touching `window` is three lines that
 * decide nothing.
 *
 * It is deliberately **not** part of `settings.ts`. That file holds what the
 * *player* chose — aim sensitivity, the three graphics dials — and its whole
 * argument is that a fill-rate decision belongs to the person looking at the
 * screen rather than to an auto-detect. This file holds what the *device* is,
 * which is not a preference and must never become a slider: nobody wants to be
 * asked how many pixels their phone has.
 *
 * The two do compose, and the order matters — the device sets the ceiling and
 * the player spends underneath it.
 */

/**
 * A device, as much of one as any decision here needs.
 *
 * Named fields rather than a `Navigator`, so a test can describe a DPR-3 phone
 * without a browser and so that adding a signal is a field rather than a mock.
 */
export interface DeviceProfile {
  /** `window.devicePixelRatio`. */
  pixelRatio: number;
  /** `(pointer: coarse)` — the *primary* pointer is a finger, not a mouse. */
  coarsePointer: boolean;
  /** `(hover: none)` — the primary pointer cannot hover. */
  cannotHover: boolean;
  /** `navigator.maxTouchPoints`. */
  touchPoints: number;
}

/**
 * Is this a handheld — a phone or a tablet rather than a desktop?
 *
 * **Three signals, and all three are needed for the same reason: each one alone
 * has a well-known false positive.** A touchscreen laptop has
 * `maxTouchPoints > 0` while its primary pointer is still a mouse. A tablet with
 * a trackpad attached reports a fine pointer. A DPR of 3 says nothing at all —
 * plenty of desktop displays are 2 and a few are 3.
 *
 * So the test is the conjunction that has no common counter-example: the primary
 * pointer is coarse, it cannot hover, and touch hardware exists. That is a
 * phone or a tablet being used as one, which is exactly the population this
 * ceiling is for.
 *
 * Note what it is deliberately not: a user-agent sniff. A UA string is a
 * moving target that has to be maintained forever and is wrong about every
 * device released after it was written, and what we actually want to know is
 * how the thing is being *held*.
 */
export function isHandheld(device: DeviceProfile): boolean {
  return device.coarsePointer && device.cannotHover && device.touchPoints > 0;
}

/**
 * How many device pixels per CSS pixel a *preview* scene may render at.
 *
 * The landing hero and the item workbench both used to ask Babylon for the
 * display's full ratio (`adaptToDeviceRatio: true`), which on a DPR-3 phone is
 * **nine times the pixels** — for a decorative turntable and a product shot
 * respectively, neither of which is the game.
 *
 * The desktop cap is 2 rather than 1, and that is the point of having two
 * numbers: an ordinary Retina display is exactly 2, so **desktop rendering is
 * unchanged** and this cannot be read as a quality cut nobody asked for. It
 * only ever bites the DPR-3 phones and the rare DPR-3 desktop, where the third
 * pixel of a 2-inch-tall render is not visible to anybody.
 *
 * The handheld cap is lower again because a phone is short of fill rate as well
 * as of pixels worth having.
 */
export const PREVIEW_PIXEL_RATIO_CAP = 2;
export const HANDHELD_PIXEL_RATIO_CAP = 1.5;

/**
 * Babylon's `hardwareScalingLevel` for a preview scene on this device.
 *
 * Inverted, because Babylon's number is CSS pixels *per* device pixel: 1 means
 * render at CSS size, 0.5 means render at twice it. Returning the level rather
 * than the ratio keeps the arithmetic in one place — a call site that inverted
 * it itself would render at a quarter of the intended resolution and look like a
 * broken texture rather than like a wrong constant.
 *
 * Never above 1: a device whose ratio is below 1 (a zoomed-out desktop) should
 * render at CSS size, not coarser than it.
 */
export function previewHardwareScaling(device: DeviceProfile): number {
  const cap = isHandheld(device) ? HANDHELD_PIXEL_RATIO_CAP : PREVIEW_PIXEL_RATIO_CAP;
  const ratio = Number.isFinite(device.pixelRatio) && device.pixelRatio > 0 ? device.pixelRatio : 1;
  return 1 / Math.max(1, Math.min(ratio, cap));
}

/**
 * Frames per second the arena's render loop should cap at, or 0 for uncapped.
 *
 * Uncapped on a desktop, and that is not a placeholder — it is the conclusion
 * `arena-scene.ts` already reached and wrote down: sub-tick interpolation gives
 * a 60 or 144 Hz display a distinct, correct position every frame, so capping to
 * the simulation rate throws those away for nothing. The stutter that capping
 * was originally tried against turned out to be the server inserting ticks the
 * client could not replay, and was fixed there.
 *
 * **That argument does not survive the trip to a phone.** A handheld is not
 * choosing between 60 and 144; it is choosing between 30 rendered frames and 40
 * rendered frames that cost twice the GPU work, drain the battery and thermally
 * throttle into something worse than 30 within a couple of minutes. Capping to
 * the simulation rate on a handheld also makes each frame exactly one tick,
 * which removes the beat between the render rate and the fixed tick as a
 * side-effect rather than as the goal.
 *
 * `__sowFps(n)` still overrides this live on either kind of device, which is
 * what makes the two comparable back to back.
 */
export const HANDHELD_TARGET_FPS = 30;

export function targetFrameRate(device: DeviceProfile): number {
  return isHandheld(device) ? HANDHELD_TARGET_FPS : 0;
}

/**
 * Whether this device may build the garage's modelled room.
 *
 * ── Measured, on the real GPU at a landscape phone viewport ─────────────────
 *
 * The room is the garage's entire memory cost and the tank is very nearly free:
 *
 *   room built      303 MB of texture across 62 uploads, 33 draw calls, 1.12 M triangles
 *   room skipped     16 MB across 7 uploads,             12 draw calls,   46 k triangles
 *
 * Nineteen times the texture residency, for the walls. The tank survives the cut
 * because the Smart Mesh roster carries **no textures at all** — a hull and a
 * turret are ~46 k triangles and one shared 512² livery pattern — so "drop the
 * 3-D scene and show a picture" would save about 10 MB more than this does and
 * would cost the one thing on the screen worth looking at.
 *
 * 303 MB matters because iOS Safari kills a tab somewhere around 250–400 MB of
 * graphics memory: that is the whole budget spent in the garage, before a
 * battlefield that wants 618 MB more. It is not a frame-rate optimisation.
 *
 * ── Why here and not on the quality dial ───────────────────────────────────
 *
 * Because this is a statement about the machine, and `settings.ts` is a statement
 * about the player. The ordering this module exists to express is that **the
 * device sets a ceiling and the dials spend underneath it** — so a phone gets the
 * roomless hangar whatever the graphics tier says, and a desktop is untouched.
 * `hangarQualityFor` clamps rather than overrides: somebody who has already
 * chosen `low` on a desktop still gets `low`.
 */
export function hangarQualityFor(
  device: DeviceProfile,
  wanted: 'high' | 'low',
): 'high' | 'low' {
  return isHandheld(device) ? 'low' : wanted;
}

/**
 * Reads the live device.
 *
 * Guarded rather than assumed: this module is imported by scene code that unit
 * tests pull in under jsdom, where `matchMedia` is absent, and a decision about
 * pixels must never be the thing that throws. Absent signals describe a plain
 * desktop, which is the conservative answer — it changes nothing.
 */
export function readDevice(): DeviceProfile {
  const media = (query: string): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  };
  return {
    pixelRatio: typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1,
    coarsePointer: media('(pointer: coarse)'),
    cannotHover: media('(hover: none)'),
    touchPoints: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints || 0,
  };
}

/** `isHandheld` against the live device. */
export const handheld = (): boolean => isHandheld(readDevice());
/** `previewHardwareScaling` against the live device. */
export const previewScaling = (): number => previewHardwareScaling(readDevice());
/** `targetFrameRate` against the live device. */
export const arenaTargetFps = (): number => targetFrameRate(readDevice());

/** The hangar tier this device allows, given the tier the player asked for. */
export const hangarQuality = (wanted: 'high' | 'low'): 'high' | 'low' =>
  hangarQualityFor(readDevice(), wanted);
